// Remove / Restore and Popular / Featured on the catalog (0331).
//
// Remove HIDES — never deletes (Kharie, 2026-10-14). Bookings point at these
// rows forever, so a removed service or duration disappears from the client app
// and stops new bookings, while every past and already-placed booking keeps
// showing what was sold. Restore puts it back exactly as it was.
//
// Popular and Featured are the client Home's two curated lists, each in its own
// order. Several services may be Featured at once.
//
// Super Admin only — the database refuses these switches from anyone else
// (0331), so other roles see the state read-only rather than a control that
// would fail. Split out of CatalogPage.tsx, which is already past the size the
// frontend rules ask to split at.

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { Switch } from "@bednbody/ui/components/switch";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  type CatalogService,
  type ServiceSwitches,
  useSetServiceSwitches,
  useSetVariantActive,
} from "./catalog.hooks";

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : "Couldn't save that change.";

/** The state chips on a service card, for every role. */
export function ServiceStateBadges({ service }: { service: CatalogService }) {
  return (
    <span className="flex flex-wrap gap-1">
      {service.is_active ? null : <Badge variant="muted">Removed</Badge>}
      {service.is_popular ? <Badge variant="primary">Popular</Badge> : null}
      {service.is_featured ? <Badge variant="accent">Featured</Badge> : null}
    </span>
  );
}

/** Popular, Featured and Remove for one service. Super Admin only. */
export function ServiceCurationControls({ service }: { service: CatalogService }) {
  const set = useSetServiceSwitches();
  const [confirming, setConfirming] = useState(false);

  function save(patch: ServiceSwitches, done: string) {
    set.mutate(
      { id: service.id, ...patch },
      {
        onSuccess: () => toast.success(done),
        onError: (error) => toast.error(errorText(error)),
      },
    );
  }

  return (
    <div className="mt-3 space-y-2 border-t border-border/60 pt-3">
      <CurationRow
        id={`popular-${service.id}`}
        label="Popular on Home"
        checked={service.is_popular}
        rank={service.popular_rank}
        disabled={set.isPending}
        onCheckedChange={(checked) =>
          save({ is_popular: checked }, checked ? "Marked Popular." : "No longer Popular.")
        }
        onRankChange={(rank) => save({ popular_rank: rank }, "Popular order saved.")}
      />
      <CurationRow
        id={`featured-${service.id}`}
        label="Featured in the Home spotlight"
        checked={service.is_featured}
        rank={service.featured_rank}
        disabled={set.isPending}
        onCheckedChange={(checked) =>
          save({ is_featured: checked }, checked ? "Featured." : "No longer Featured.")
        }
        onRankChange={(rank) => save({ featured_rank: rank }, "Featured order saved.")}
      />

      {service.is_active ? (
        <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
          Remove from catalog
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          disabled={set.isPending}
          onClick={() => save({ is_active: true }, `${service.name} is back in the catalog.`)}
        >
          Restore to catalog
        </Button>
      )}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {service.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Clients stop seeing it and it can&apos;t be booked again. Nothing is deleted: past
              bookings and bookings already placed keep it, and Restore brings it back with the same
              durations and prices.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                save({ is_active: false }, `${service.name} removed from the catalog.`)
              }
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CurationRow({
  id,
  label,
  checked,
  rank,
  disabled,
  onCheckedChange,
  onRankChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  rank: number | null;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
  onRankChange: (rank: number | null) => void;
}) {
  const [draft, setDraft] = useState(rank === null ? "" : String(rank));
  useEffect(() => setDraft(rank === null ? "" : String(rank)), [rank]);

  function commitRank() {
    const next = draft.trim() === "" ? null : Number(draft);
    if (next !== null && (!Number.isInteger(next) || next < 0)) {
      toast.error("The order has to be a whole number, 0 or more.");
      setDraft(rank === null ? "" : String(rank));
      return;
    }
    if (next !== rank) onRankChange(next);
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <Label htmlFor={id} className="text-sm font-normal text-muted-foreground">
        {label}
      </Label>
      <span className="flex items-center gap-2">
        {checked ? (
          <Input
            aria-label={`${label} — order`}
            className="h-7 w-16 tabular-nums"
            inputMode="numeric"
            min={0}
            onBlur={commitRank}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Order"
            step="1"
            type="number"
            value={draft}
          />
        ) : null}
        <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
      </span>
    </div>
  );
}

/** Remove / Restore for one duration. Super Admin only. */
export function VariantActiveToggle({
  variant,
  serviceName,
}: {
  variant: CatalogService["service_variants"][number];
  serviceName: string;
}) {
  const set = useSetVariantActive();
  const label = `${serviceName} ${variant.duration_minutes} min`;

  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={set.isPending}
      onClick={() =>
        set.mutate(
          { id: variant.id, active: !variant.active },
          {
            onSuccess: () =>
              toast.success(variant.active ? `${label} removed.` : `${label} restored.`),
            onError: (error) => toast.error(errorText(error)),
          },
        )
      }
    >
      {variant.active ? "Remove" : "Restore"}
    </Button>
  );
}
